import React, { useEffect, useState } from "react";
import { useLang } from "../../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Trash2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";

const COLUMNS = [
  { key: "submitted", label_fr: "À examiner", label_en: "To review" },
  { key: "under_review", label_fr: "En examen", label_en: "Under review" },
  { key: "evaluation_sent", label_fr: "Évaluation envoyée", label_en: "Evaluation sent" },
  { key: "evaluation_received", label_fr: "Évaluation reçue", label_en: "Evaluation received" },
  { key: "accepted", label_fr: "Acceptée", label_en: "Accepted" },
  { key: "rejected", label_fr: "Refusée", label_en: "Rejected" },
];

const HELP_FR = {
  submitted: "Nouvelles candidatures payées (75€) — à prendre en charge",
  under_review: "Dossiers en cours d'examen par l'équipe",
  evaluation_sent: "Évaluation envoyée à la famille — en attente du retour",
  evaluation_received: "Évaluation reçue — à valider pour décision finale",
  accepted: "Élèves acceptés — en attente d'inscription / paiement de l'acompte",
  rejected: "Candidatures refusées",
};
const HELP_EN = {
  submitted: "New paid (€75) applications — to take up",
  under_review: "Files being reviewed by the team",
  evaluation_sent: "Evaluation sent to family — awaiting response",
  evaluation_received: "Evaluation received — pending final decision",
  accepted: "Accepted students — awaiting enrollment / deposit",
  rejected: "Rejected applications",
};

export default function AdminAdmissions() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [apps, setApps] = useState([]);
  const HELP = lang === "fr" ? HELP_FR : HELP_EN;

  const load = () => api.get("/applications").then(r => setApps(r.data));
  useEffect(() => { load(); }, []);

  const move = async (id, status) => {
    try {
      await api.patch(`/applications/${id}/status`, { status });
      toast.success(`→ ${t(`statuses.${status}`)}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (a) => {
    const name = `${a.student?.first_name || ""} ${a.student?.last_name || ""}`.trim();
    const msg = lang === "fr"
      ? `Supprimer la candidature de "${name}" ?\n\nCela supprimera également l'élève et tout l'historique. Irréversible.`
      : `Delete "${name}"'s application?\n\nThis also removes the student and full history. Irreversible.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/applications/${a.id}`);
      toast.success(lang === "fr" ? `"${name}" supprimé` : `"${name}" deleted`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-admissions">
      <p className="overline mb-1">OFA France · Admin</p>
      <h1 className="font-serif text-3xl tracking-tight mb-2">{t("admin.admissions")}</h1>
      <p className="text-sm text-muted-foreground mb-6 max-w-3xl">
        {lang === "fr"
          ? "Pipeline de gestion des candidatures payées. Chaque colonne représente une étape — faites glisser un dossier vers la colonne suivante en cliquant sur la flèche correspondante."
          : "Pipeline for paid applications. Each column is a stage — move a file by clicking the arrow to the next column."}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        {COLUMNS.map(col => {
          const list = apps.filter(a => a.status === col.key);
          const label = lang === "fr" ? col.label_fr : col.label_en;
          return (
            <div key={col.key} data-testid={`col-${col.key}`} className="bg-secondary/30 rounded-md p-3 min-h-[400px]">
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs uppercase tracking-wider font-semibold">{label}</div>
                  <Badge variant="outline">{list.length}</Badge>
                </div>
                <div className="text-[10px] text-muted-foreground leading-snug">{HELP[col.key]}</div>
              </div>
              <div className="space-y-2">
                {list.map(a => (
                  <Card key={a.id} className="bg-card">
                    <CardContent className="p-3 text-sm">
                      <div className="font-medium">{a.student?.first_name} {a.student?.last_name}</div>
                      <div className="text-xs text-muted-foreground">{a.family?.country} · {a.program_choice ? t(`programs.${a.program_choice}`) : "—"}</div>
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {COLUMNS.filter(c => c.key !== a.status).map(c => (
                          <button key={c.key} data-testid={`move-${a.id}-${c.key}`} onClick={() => move(a.id, c.key)} className="text-[10px] px-2 py-0.5 rounded border border-border hover:bg-secondary text-muted-foreground">→ {lang === "fr" ? c.label_fr : c.label_en}</button>
                        ))}
                        {isSuper && (
                          <button
                            data-testid={`delete-${a.id}`}
                            onClick={() => remove(a)}
                            className="text-[10px] px-2 py-0.5 rounded border border-red-300 text-red-600 hover:bg-red-50 inline-flex items-center gap-1"
                            title={lang === "fr" ? "Supprimer (super admin)" : "Delete (super admin)"}
                          >
                            <Trash2 className="w-3 h-3" />
                            {lang === "fr" ? "Suppr." : "Del"}
                          </button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
